/**
 * "Show where": the producers' side of a locate link (twin slice 1, plan
 * W4 steps 13 and 14).
 *
 * Toasts, bell rows and drawer headers all point at the same place the
 * same way: a locate link (`locate.ts`) to the Overview, pushed with a
 * fresh landing id. A link only POINTS. It never opens a drawer -- nothing
 * here dispatches the `nx:alert-deep-link` event the old click-through
 * used -- and it never carries a camera pose.
 *
 * THE VIEW. Decision 7 (the top of the precedence order): "deep links open
 * Building". So every link here names its view, and it is `3d` unless the
 * producer knows the Building view cannot be shown:
 *
 * - toasts, bell rows and the OS notification: always `view=3d`, like the
 *   Chat "Where:" line (contract §3). Their words ("Show where") promise
 *   no view, and where the Building view can't be drawn the Overview shows
 *   the Map and says why (`twin-availability.ts`, plan W4 step 7);
 * - drawer headers: the catalogue's own words name the view ("Show in
 *   building" or "Show on map", §8), so the link must go where its words
 *   say. It is `3d` when nothing this page knows rules the Building view
 *   out, and `2d` -- "Show on map" -- when something does (the flag, the
 *   role, no building model for this site, a screen under 768 px).
 *
 * LANDING IDENTITY (plan W4 step 10, for the Overview in stage 4). A
 * landing is the initial load with locate parameters, or a PUSH carrying
 * `state.nxLanding = <id>`. In-view moves use `replace` and carry no id,
 * so react-router minting a new `location.key` on every replace never
 * reads as a landing. Each producer mints a NEW id per press: pressing
 * "Show where" twice on the same thing is two landings, and the Overview
 * remembers only the last id it handled.
 */
import type { AlertViewModel } from '../api/adapters';
import { parseDeepLink } from './alert-deep-link';
import type { TwinAvailability } from './twin-availability';
import {
  formatFocus,
  formatFrom,
  locateHref,
  LocateLinkError,
  type LocateFocus,
  type LocateFrom,
} from './locate';

/** The history-state key a landing push carries. */
export const LANDING_STATE_KEY = 'nxLanding';

export interface LandingState {
  readonly [LANDING_STATE_KEY]: string;
}

let landingSeq = 0;

/** A history state for one landing push, with an id no other push has. */
export function newLandingState(): LandingState {
  landingSeq += 1;
  return { [LANDING_STATE_KEY]: `${Date.now().toString(36)}-${landingSeq.toString(36)}` };
}

/** The landing id a location's state carries; null for anything else. */
export function landingIdOf(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const id = (state as Record<string, unknown>)[LANDING_STATE_KEY];
  return typeof id === 'string' && id !== '' ? id : null;
}

/** The view a "Show where" link names. Always one: Decision 7. */
export type ShowWhereView = '2d' | '3d';

/**
 * The view an alert's "Show where" opens -- toast, bell row, OS
 * notification: the Building view, as every deep link does (Decision 7),
 * and as the Chat "Where:" line does (contract §3).
 */
export const ALERT_SHOW_WHERE_VIEW: ShowWhereView = '3d';

/** A drawer header's words for each view (catalogue §8). */
export const SHOW_IN_BUILDING = 'Show in building';
export const SHOW_ON_MAP = 'Show on map';

/**
 * A drawer header's link: the Building view and "Show in building" when
 * nothing rules it out here, the Map and "Show on map" when something
 * does. `availability` is `twinAvailability` with what the page knows; a
 * graphics failure that only a canvas can report is the Overview's to say.
 */
export function drawerShowWhere(availability: TwinAvailability): {
  view: ShowWhereView;
  label: string;
} {
  return availability.available
    ? { view: '3d', label: SHOW_IN_BUILDING }
    : { view: '2d', label: SHOW_ON_MAP };
}

/**
 * The locate link that shows `focus` at `site` in `view`, or null when no
 * link may be built (no site: `locateHref` refuses one, and a link without
 * a site is not a link). Anything else `locateHref` throws is a bug, and
 * throws. The arguments are in the URL's own order.
 */
export function showWhereHref(
  site: string | null | undefined,
  view: ShowWhereView,
  focus: LocateFocus,
  from: LocateFrom | null = null,
): string | null {
  try {
    return locateHref({
      site,
      view,
      focus: formatFocus(focus),
      from: from ? formatFrom(from) : null,
    });
  } catch (err) {
    if (err instanceof LocateLinkError) return null;
    throw err;
  }
}

/** The alert fields `alertPlaceFocus` reads. */
export type PlaceableAlert = Pick<
  AlertViewModel,
  'id' | 'sourceKind' | 'sourceId' | 'deepLink' | 'robotName' | 'taskId'
>;

/**
 * What a "Show where" on this alert points at, or null when the alert is
 * not about one place in the building (a gateway, a door adapter, a whole
 * site) -- the same four sources the Chat "Where:" line covers: robot,
 * lift, door and task.
 *
 * The SUBJECT when it can be named without guessing: `lift:SL1`,
 * `door:<name>`, `task:<id>`, and `robot:<fleet>/<name>` when the row
 * carries the fleet. A robot alert names only the bare robot today
 * (`alerts_engine.py` keys it so), and a link never carries a bare robot
 * name (two fleets may hold the same one); that one points at the ALERT,
 * and the resolver fleet-qualifies it from the roster
 * (`locate-resolve.ts`, alert focus).
 */
export function alertPlaceFocus(alert: PlaceableAlert): LocateFocus | null {
  // `sourceKind:sourceId` first -- `adaptAlert` synthesises `deepLink`
  // from them only when the row carried no `deep_link` of its own, and
  // the structured pair is the server's own statement of the subject.
  const structured =
    alert.sourceKind && alert.sourceId ? `${alert.sourceKind}:${alert.sourceId}` : undefined;
  let link = parseDeepLink(structured) ?? parseDeepLink(alert.deepLink);
  if (!link && alert.robotName) link = { kind: 'robot', robotName: alert.robotName };
  if (!link && alert.taskId) link = { kind: 'task', taskId: alert.taskId };
  if (!link) return null;
  switch (link.kind) {
    case 'lift':
      return { kind: 'lift', liftName: link.liftName };
    case 'door':
      return { kind: 'door', doorName: link.doorName };
    case 'task':
      return { kind: 'task', taskId: link.taskId };
    case 'robot':
      return link.fleetName
        ? { kind: 'robot', fleetName: link.fleetName, robotName: link.robotName }
        : alert.id
          ? { kind: 'alert', alertId: alert.id }
          : null;
  }
}

/**
 * The focus and origin a press on this alert lands with: its place when it
 * has one, the alert itself otherwise -- where the Overview says out loud
 * "This alert isn't about one place in the building." -- and always
 * `from=alert:<id>`, for the Back chip. Null only for a row with no id.
 */
export function alertShowWhere(
  alert: PlaceableAlert,
): { focus: LocateFocus; from: LocateFrom } | null {
  if (!alert.id) return null;
  return {
    focus: alertPlaceFocus(alert) ?? { kind: 'alert', alertId: alert.id },
    from: { kind: 'alert', alertId: alert.id },
  };
}
